import { Banner, SwitchLineInput, Box, Flex } from '@sequencehq/core-components'
import { FC } from 'react'
import {
  BorderRadius6,
  GreyGrey10,
  GreyGrey70,
  GreyGrey80,
  IndigoIndigo60,
  Link,
  Primary13Regular,
  Primary13SemiBold
} from '@sequencehq/core-components'
import {
  StripeInspectorItemData,
  useStripePaymentWidget
} from './useStripePaymentWidget'
import { EXCLUSIVE_PAYMENT_PROVIDER_MESSAGE } from 'Integrations/constants'

/**
 * Which invoices the toggle affects. On a schedule it only applies to invoices
 * still to be generated, so the copy has to say so.
 */
type StripePaymentScope = 'invoice' | 'billingSchedule'

const PAYMENT_LINK_MESSAGE: Record<StripePaymentScope, string> = {
  invoice:
    'When enabled, a Stripe payment link is added to invoices. Your customer may be auto-charged for this invoice if auto-charging is enabled,',
  billingSchedule:
    'When enabled, a Stripe payment link is added to future invoices on this schedule. Your customer may be auto-charged for those invoices if auto-charging is enabled,'
}

interface StripePaymentInspectorItemProps {
  fieldData: {
    acceptPaymentsViaStripe: {
      value: boolean
      disabled?: boolean
    }
  }
  onChange: (newData: StripeInspectorItemData) => void
  disabled?: boolean
  conflictingProviderActive: boolean
  scope: StripePaymentScope
}

export const StripePaymentWidget: FC<
  StripePaymentInspectorItemProps
> = props => {
  const { fields } = useStripePaymentWidget({
    dataFromInterface: {
      acceptPaymentsViaStripe: props.fieldData.acceptPaymentsViaStripe
    },
    onChange: props.onChange,
    disabled: props.disabled
  })

  return (
    <Flex flexDirection="column" gap="8px">
      <Box {...Primary13SemiBold} color={GreyGrey80}>
        Stripe payments
      </Box>
      {props.conflictingProviderActive && (
        <Banner
          variant="warning"
          data-testid="stripe.inspector.content.exclusiveProviderBanner"
        >
          {EXCLUSIVE_PAYMENT_PROVIDER_MESSAGE}
        </Banner>
      )}
      <Box
        borderRadius={BorderRadius6}
        backgroundColor={GreyGrey10}
        padding="8px"
      >
        <SwitchLineInput
          isDisabled={fields.acceptPaymentsViaStripe.isDisabled}
          text="Accept payments via Stripe"
          value={fields.acceptPaymentsViaStripe.value}
          onChange={fields.acceptPaymentsViaStripe.onChange}
          styles={{
            label: {
              ...Primary13Regular,
              color: GreyGrey80
            }
          }}
          data-testid="stripe.inspector.content.acceptPayments"
        />
      </Box>
      <Box
        {...Primary13Regular}
        color={GreyGrey70}
        data-testid="stripe.inspector.content.paymentLinkMessage"
      >
        {PAYMENT_LINK_MESSAGE[props.scope]}{' '}
        <Link
          to="https://docs.sequencehq.com/connectors/stripe"
          target="_blank"
          rel="noreferrer"
          color={IndigoIndigo60}
        >
          learn more.
        </Link>
      </Box>
    </Flex>
  )
}
